import { useState } from "react";
import { api, type APIResult } from "../../api/client";
import { useAppState } from "../../hooks/useAppState";
import { PageHeader } from "../PageHeader";
import { ResponseCard } from "../ResponseCard";
import { SectionCard } from "../SectionCard";
import type { GeocodeResponse, ReverseGeocodeResponse } from "../../types/routecore";

// Tab integrando geocode + reverse geocode em duas colunas, já que são
// rápidos e complementares.
type Props = {
  onNavigate: (tabId: string) => void;
};

export function TabGeocode({ onNavigate }: Props) {
  const [address, setAddress] = useState("Av. Getúlio Vargas, Centro, Chapecó");
  const [country, setCountry] = useState("BR");
  const [limit, setLimit] = useState(5);
  const [geoResult, setGeoResult] = useState<APIResult<GeocodeResponse> | null>(null);
  const [geoLoading, setGeoLoading] = useState(false);

  const [lat, setLat] = useState("-27.0996");
  const [lon, setLon] = useState("-52.6188");
  const [revResult, setRevResult] = useState<APIResult<ReverseGeocodeResponse> | null>(null);
  const [revLoading, setRevLoading] = useState(false);

  // Hand-off para o mapa vetorial: guarda o ponto no AppState e troca de aba.
  // O TabMapVector consome esse ponto ao montar (marker + flyTo + popup).
  const { setPendingMapPoint } = useAppState();

  async function runGeo() {
    setGeoLoading(true);
    setGeoResult(null);
    const r = await api.geocode({
      address,
      country_code: country || undefined,
      limit: limit > 0 ? limit : undefined,
    });
    setGeoResult(r as APIResult<GeocodeResponse>);
    setGeoLoading(false);
  }

  async function runReverse() {
    const latNum = parseFloat(lat);
    const lonNum = parseFloat(lon);
    if (Number.isNaN(latNum) || Number.isNaN(lonNum)) return;
    setRevLoading(true);
    setRevResult(null);
    const r = await api.reverseGeocode({ lat: latNum, lon: lonNum });
    setRevResult(r as APIResult<ReverseGeocodeResponse>);
    setRevLoading(false);
  }

  function copyToReverse(lat: number, lon: number) {
    setLat(lat.toString());
    setLon(lon.toString());
  }

  // Abre o ponto na aba "Mapa (vetorial)": registra o ponto pendente no
  // AppState e navega. O mapa lá posiciona o pino e voa até ele.
  function openOnMap(lat: number, lon: number, label: string) {
    setPendingMapPoint({ lat, lon, label });
    onNavigate("map-vector");
  }

  // Local para preservar o narrowing dentro do onClick (o TS não mantém a
  // estreita de `revResult.data` em closures; uma const local, sim).
  const rev = revResult?.data;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Geocoding"
        subtitle="Conversão entre endereço e coordenadas, com cache Redis no upstream."
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <SectionCard
          title="Geocode (endereço → coords)"
          description="POST /v1/geocode — repassa para o Nominatim."
          actions={
            <button
              type="button"
              className="ks-btn-primary text-xs"
              disabled={geoLoading || !address}
              onClick={runGeo}
            >
              {geoLoading ? "buscando..." : "Buscar"}
            </button>
          }
        >
          <div className="space-y-2">
            <div>
              <label className="ks-label">Endereço</label>
              <input
                type="text"
                className="ks-input"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              <div>
                <label className="ks-label">Country code</label>
                <input
                  type="text"
                  className="ks-input ks-code"
                  maxLength={2}
                  value={country}
                  onChange={(e) => setCountry(e.target.value.toUpperCase())}
                />
              </div>
              <div>
                <label className="ks-label">Limit</label>
                <input
                  type="number"
                  className="ks-input"
                  min={1}
                  max={10}
                  value={limit}
                  onChange={(e) => setLimit(parseInt(e.target.value, 10) || 1)}
                />
              </div>
            </div>
          </div>
          {geoResult?.data?.results && geoResult.data.results.length > 0 && (
            <ul className="mt-3 space-y-1">
              {geoResult.data.results.map((r, i) => (
                <li
                  key={`${r.location.lat}-${r.location.lon}-${i}`}
                  className="flex items-center justify-between gap-2 rounded-md border border-ink-200 bg-white px-3 py-2 text-sm"
                >
                  <div className="min-w-0">
                    <p className="truncate text-ink-900">{r.formatted_address}</p>
                    <p className="text-[11px] font-mono text-ink-500">
                      {r.location.lat.toFixed(5)}, {r.location.lon.toFixed(5)}
                      {r.place_type ? ` · ${r.place_type}` : ""}
                      {Number.isFinite(r.confidence) ? ` · ${(r.confidence * 100).toFixed(0)}%` : ""}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <button
                      type="button"
                      className="ks-btn-ghost text-xs"
                      onClick={() =>
                        openOnMap(r.location.lat, r.location.lon, r.formatted_address)
                      }
                    >
                      Abrir no Mapa
                    </button>
                    <button
                      type="button"
                      className="ks-btn-ghost text-xs"
                      onClick={() => copyToReverse(r.location.lat, r.location.lon)}
                    >
                      usar →
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>

        <SectionCard
          title="Reverse geocode (coords → endereço)"
          description="POST /v1/reverse-geocode"
          actions={
            <button
              type="button"
              className="ks-btn-primary text-xs"
              disabled={revLoading || !lat || !lon}
              onClick={runReverse}
            >
              {revLoading ? "buscando..." : "Buscar"}
            </button>
          }
        >
          <div className="grid gap-2 sm:grid-cols-2">
            <div>
              <label className="ks-label">Latitude</label>
              <input
                type="text"
                className="ks-input ks-code"
                value={lat}
                onChange={(e) => setLat(e.target.value)}
              />
            </div>
            <div>
              <label className="ks-label">Longitude</label>
              <input
                type="text"
                className="ks-input ks-code"
                value={lon}
                onChange={(e) => setLon(e.target.value)}
              />
            </div>
          </div>
          {rev && (
            <div className="mt-3 rounded-md border border-ink-200 bg-white px-3 py-2 text-sm">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold text-ink-900">{rev.formatted_address}</p>
                  <p className="text-[11px] font-mono text-ink-500">
                    {rev.location.lat.toFixed(5)}, {rev.location.lon.toFixed(5)}
                  </p>
                </div>
                <button
                  type="button"
                  className="ks-btn-ghost shrink-0 text-xs"
                  onClick={() =>
                    openOnMap(rev.location.lat, rev.location.lon, rev.formatted_address)
                  }
                >
                  Abrir no Mapa
                </button>
              </div>
              {rev.components && (
                <dl className="mt-2 grid gap-1 text-xs sm:grid-cols-2">
                  {Object.entries(rev.components).map(([k, v]) => (
                    <div key={k} className="flex gap-1">
                      <dt className="text-ink-500">{k}:</dt>
                      <dd className="text-ink-800">{v}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </div>
          )}
        </SectionCard>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <ResponseCard result={geoResult} loading={geoLoading} />
        <ResponseCard result={revResult} loading={revLoading} />
      </div>
    </div>
  );
}
